import React, { createContext, useContext, useEffect, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { isSuperAdmin } from '../config/adminAccess';

interface SocketContextType {
    socket: Socket | null;
    isConnected: boolean;
}

const SocketContext = createContext<SocketContextType>({ socket: null, isConnected: false });

export const useSocket = () => useContext(SocketContext);

export const SocketProvider = ({ children }: { children: React.ReactNode }) => {
    const { token, role } = useAuth();
    const [socket, setSocket] = useState<Socket | null>(null);
    const [isConnected, setIsConnected] = useState(false);
    const superAdmin = isSuperAdmin(role);

    useEffect(() => {
        // No session yet (or logged out) — nothing to connect. The live feed is the admin room,
        // which the server only lets super admins join, so a marketing login doesn't connect.
        if (!token || !superAdmin) {
            setSocket(null);
            setIsConnected(false);
            return;
        }

        // Use environment variable for Socket URL, stripping /api if present
        const apiUrl = import.meta.env.VITE_API_URL || '';
        const socketUrl = apiUrl.replace(/\/api\/?$/, '');

        console.log('🔌 Admin Connecting to Socket:', socketUrl);

        const newSocket = io(socketUrl, {
            transports: ['websocket'],
            // Never give up: with 5 attempts a short API restart/deploy left the live map, bells and finance
            // cards dead until the page was reloaded.
            reconnectionAttempts: Infinity,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            auth: { token },
        });

        newSocket.on('connect', () => {
            console.log('Admin Socket Connected:', newSocket.id);
            setIsConnected(true);

            // Join admin room if backend supports it
            newSocket.emit('join_admin_room');
        });

        newSocket.on('disconnect', (reason) => {
            console.log('Admin Socket Disconnected:', reason);
            setIsConnected(false);
            // The server closed us: socket.io does not reconnect after that on its own.
            if (reason === 'io server disconnect') {
                setTimeout(() => newSocket.connect(), 1000);
            }
        });

        newSocket.on('connect_error', (err) => {
            console.warn('Admin Socket connection error:', err.message);
        });

        setSocket(newSocket);

        return () => {
            newSocket.disconnect();
        };
    }, [token, superAdmin]);

    return (
        <SocketContext.Provider value={{ socket, isConnected }}>
            {children}
        </SocketContext.Provider>
    );
};
